// The desk's offer facts — `pms_desk_offer` (PB3, PMS-PHONE-BOOKING-BRIEF §5).
//
// Browser-scoped: the signed-in desk's client, gated in the RPC by
// `pms.reservation.write` and the établissement in reach. The facts are
// `pms_public_offer`'s plus `superior` per category and `lodgeBigger`; the
// shape is checked rather than trusted, like the public adapter's, because a
// field the pricing reads and the RPC stopped sending would price as NaN.

import { childMealsProblem, err, ok, type DeskOfferFacts, type DeskOfferRepository, type Result } from '@lemontaigu/pms-core';
import { toPmsError, type SupabaseClient } from './support.js';

export class SupabaseDeskOfferRepository implements DeskOfferRepository {
  constructor(private readonly db: SupabaseClient) {}

  async offer(propertyCode: string, from: string, to: string): Promise<Result<DeskOfferFacts>> {
    const r = await this.db.rpc('pms_desk_offer', { p_property_code: propertyCode, p_from: from, p_to: to });
    if (r.error) {
      return r.error.code === '42501'
        ? err('forbidden', 'Établissement hors de votre périmètre, ou droit de réservation manquant.')
        : { ok: false, error: toPmsError(r.error) };
    }
    const facts = r.data as DeskOfferFacts | null;
    if (
      facts === null ||
      typeof facts.lodgeBigger !== 'boolean' ||
      !Array.isArray(facts.categories) ||
      facts.categories.some((c) => typeof c.superior !== 'boolean') ||
      !Array.isArray(facts.plans) ||
      !Array.isArray(facts.hotelClosedNights) ||
      !Array.isArray(facts.extras)
    ) {
      return err('integration', 'pms_desk_offer: réponse inattendue');
    }
    const child = facts.profile?.childMealsDirect;
    if (child != null && childMealsProblem(child) !== null) {
      return err('integration', 'pms_desk_offer: repas enfants inattendus');
    }
    return ok(facts);
  }
}
